<!-- eslint-disable-line vue/multi-word-component-names -->
<template>
  <div class="w-full h-full pt-[47px] flex flex-col justify-center items-center">
    <!-- EngagedSquadView anchor -->
    <div
      v-if="!state.active_caverns_run"
      id="caverns_engaged_heroes_view"
      class="mt-[3%] h-[20vh] w-[800px] min-w-[68vw] max-w-[calc(100vw-40px)] py-1 px-3 bg-white/10 rounded-md"
    >
      <div
        :class="{
          'text-xl font-bold underline text-zink-400 no-select': true,
        }"
      >
        {{ text(`ui.game_submodes.${state.current_game_submode}`) }}
        {{ text('ui.caverns_squad') }}:
      </div>
    </div>

    <!-- surface controls -->
    <div
      v-if="!state.active_caverns_run"
      class="mt-2 p-2 border border-zinc-700 border-t-0 rounded-md"
    >
      <div id="caverns_submode_info_bar" class="w-full px-4 text-center">
        <!-- e.g. 3-man Deepest Level: 27 -->
        <div class="text-yellow-300">
          {{ text(`ui.game_submodes.${state.current_game_submode}`) }}
          {{ text('ui.deepest_level') }}: {{ state.deepest_level }}
        </div>
      </div>
      <div
        id="caverns_buttons_panel"
        :class="{
          'mt-2 h-18 w-[440px] p-2 flex justify-center': true,
          'items-center': state.portal_level,
          'items-end': !state.portal_level,
        }"
      >
        <div>
          <DTButton
            id="enter_at_1_button"
            type="info"
            :class="{
              'w-[210px] py-1': true,
              'pointer-events-none': state.is_on_tutorial_step_nav_to_edit_hero,
            }"
            :disabled="state.is_on_tutorial_step_nav_to_edit_hero || state.pending_enter_caverns"
            @click="onDescendCavernsAt1Click"
          >
            <DTAnimatedElipsis v-if="state.pending_enter_caverns" class="text-2xl font-extrabold" />
            <span v-else class="text-sm">
              {{ text('ui.descend_caverns_at_1') }}
            </span>
          </DTButton>
          <div v-if="state.portal_level" class="w-full text-right">
            <DTButton
              type="portal"
              class="mt-2 py-1 w-[210px]"
              :disabled="state.pending_enter_caverns"
              @click="onDescendCavernsAtPortalLevelClick"
            >
              <DTAnimatedElipsis
                v-if="state.pending_enter_caverns"
                class="text-2xl font-extrabold"
              />
              <span v-else class="px-2 text-sm">
                {{ text('ui.descend_caverns_at_portal_level')(state.portal_level) }}
              </span>
            </DTButton>
            <span class="mr-3 text-sm text-yellow-300">
              {{
                Tools.formatGameCurrency(Economy.CAVERNS_FEE_PER_LEVEL * state.portal_level) +
                ' ' +
                text('ui.gold_brief')
              }}
            </span>
          </div>
        </div>
        <div class="ml-8 text-center">
          <CavernsPassiveYieldView />
          <DTButton
            type="neutral"
            :class="{
              'w-[150px] py-1 px-5': true,
              'mb-[25px]': state.portal_level && state.deepest_level > 1,
            }"
            :disabled="
              state.pending_claim_delay || state.pending_claim_call || state.pending_enter_caverns
            "
            @click="
              () => {
                state.pending_claim_call = true;
                CavernsActions.claimPassiveYields();
              }
            "
          >
            <DTAnimatedElipsis
              v-if="state.pending_claim_call || state.pending_enter_caverns"
              class="text-2xl font-extrabold"
            />
            <span v-else-if="state.pending_claim_delay">
              {{ state.tickers.claim_delay_text }}
            </span>
            <span v-else>{{ text('ui.claim') }}</span>
          </DTButton>
        </div>
      </div>
    </div>

    <!-- active caverns battle view -->
    <div
      v-if="state.active_caverns_run"
      id="caverns_battle_root"
      class="w-full h-full relative flex justfy-center"
    >
      <div id="caverns_left_sidebar" class="w-[33%] h-full absolute left-[18px]"></div>
      <div id="caverns_battle_body_wrapper" class="w-full h-full relative flex justify-center">
        <div id="caverns_battle_window" class="w-[34%] h-[60vh] mt-6"></div>
      </div>
      <div class="w-[26%] h-[calc(100%-6vh)] absolute top-[5vh] right-[20px]">
        <CavernsBattleLog />
      </div>
    </div>
  </div>
</template>

<script setup>
import Economy from 'dt-common/constants/Economy';
import getNumHeroesInSubmode from 'dt-common/isomorphic-helpers/getNumHeroesInSubmode';
import firebase from 'firebase/compat/app';
import { nextTick, onBeforeUnmount, onMounted, reactive } from 'vue';
import Audio from '~/Audio';
import { CAVERNS_BATTLE_INIT_DELAY } from '~/caverns-worker';
import { CavernsActions, HeroBuildActions, TutorialActions, UIActions } from '~/flux/actions';
import {
  ApplicationStore,
  CavernsStore,
  CurrencyStore,
  GameStateStore,
  HeroBuildStore,
  TutorialStore,
  UIStore,
} from '~/flux/stores';
import text from '~/text';
import Tools from '~/Tools';
import CanvasTools from '~/view/CanvasTools';
import {
  EngagedSquadView,
  GameSubmodeSelector,
  SquadPuppetMouseoverPanel,
} from '~/view/components/common/canvas';
import { DTAnimatedElipsis, DTButton } from '~/view/components/common/DOM';
import CavernsBattleWindow from '../canvas/CavernsBattleWindow';
import UnitDetailsPanel from '../canvas/UnitDetailsPanel';
import CavernsBattleLog from './CavernsBattleLog.vue';
import CavernsPassiveYieldView from './CavernsPassiveYieldView.vue';

const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
let _battle_window;
let _engaged_squad_view;
let _gameSubmodeSelector;
let _squad_puppet_mouseover_panel;
let _tickersInterval;
let _unit_details_panel;

const { caverns_data } = CavernsStore.getAll();
const { current_game_submode } = UIStore.getAll();
const state = reactive({
  active_caverns_run: false,
  current_game_submode: current_game_submode,
  pending_claim_call: false,
  pending_claim_delay: false,
  pending_enter_caverns: false,
  portal_level: caverns_data[current_game_submode].portal_level,
  deepest_level: caverns_data[current_game_submode].deepest_level,
  tickers: {
    claim_delay_text: '???',
  },
  is_on_tutorial_step_nav_to_edit_hero: false,
  showing_debrief_modal: false,
});

let _tutorial_timeout;

onMounted(() => {
  nextTick(() => {
    makeGameSubmodeSelector();
    makeEngagedSquadView();
  });

  ApplicationStore.on(ApplicationStore.API_ERROR, onAPIError);
  CavernsStore.on(CavernsStore.CAVERNS_BATTLE_INITIALIZED, onCavernsBattleInitialized);
  CavernsStore.on(CavernsStore.CAVERNS_PORTAL_LEVEL_SET, onPortalLevelSet);
  CavernsStore.on(CavernsStore.ENTERED_CAVERNS, onEnteredCaverns);
  CavernsStore.on(CavernsStore.CAVERNS_TRAVEL_SUCCESS, onCavernsTravelSuccess);
  CavernsStore.on(CavernsStore.RETURNED_TO_SURFACE, onReturnedToSurface);
  CavernsStore.on(CavernsStore.MISSING_CAVERNS_RUN_DATA, onReturnedToSurface);
  CavernsStore.on(CavernsStore.PASSIVE_YIELDS_CLAIMED, onPassiveYieldsClaimed);
  CurrencyStore.on(CurrencyStore.INSUFFICIENT_GOLD, onInsufficientGold);
  GameStateStore.on(GameStateStore.LOADOUT_UPDATED, onLoadoutUpdated);
  UIStore.on(UIStore.GAME_SUBMODE_SELECTION, onGameSubmodeSelection);
  UIStore.on(UIStore.MODAL_CLOSED, onModalClosed);
  TutorialStore.on(TutorialStore.TUTORIAL_SPOTLIGHT_VISIBLE, onTutorialSpotlightVisible);
  TutorialStore.on(TutorialStore.TUTORIAL_SPOTLIGHT_HIDDEN, onTutorialSpotlightHidden);
  TutorialStore.on(TutorialStore.TUTORIAL_SKIPPED, onTutorialSkipped);

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  Audio.setBGTrack('menu_music');

  _tickersInterval = setInterval(calcTickers, 1000);
  calcTickers();

  // tutorial step 2 - enter caverns
  const { entered_caverns, skipped_all } = TutorialStore.getCompletedTutorialSteps();
  if (!entered_caverns && !skipped_all) {
    const enter_at_1_button = document.getElementById('enter_at_1_button');
    const rect = enter_at_1_button.getBoundingClientRect();
    TutorialActions.makeTutorialSpotlight({
      radius: 160,
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height,
    });
  }
});

onBeforeUnmount(() => {
  destroyBattleWindow();
  destroyEngagedSquadView();
  destroyGameSubmodeSelector();
  destroySquadPuppetMouseoverPanel();
  destroyUnitDetailsPanel();

  ApplicationStore.removeListener(ApplicationStore.API_ERROR, onAPIError);
  CavernsStore.removeListener(CavernsStore.CAVERNS_BATTLE_INITIALIZED, onCavernsBattleInitialized);
  CavernsStore.removeListener(CavernsStore.CAVERNS_PORTAL_LEVEL_SET, onPortalLevelSet);
  CavernsStore.removeListener(CavernsStore.ENTERED_CAVERNS, onEnteredCaverns);
  CavernsStore.removeListener(CavernsStore.CAVERNS_TRAVEL_SUCCESS, onCavernsTravelSuccess);
  CavernsStore.removeListener(CavernsStore.RETURNED_TO_SURFACE, onReturnedToSurface);
  CavernsStore.removeListener(CavernsStore.MISSING_CAVERNS_RUN_DATA, onReturnedToSurface);
  CavernsStore.removeListener(CavernsStore.PASSIVE_YIELDS_CLAIMED, onPassiveYieldsClaimed);
  CurrencyStore.removeListener(CurrencyStore.INSUFFICIENT_GOLD, onInsufficientGold);
  GameStateStore.removeListener(GameStateStore.LOADOUT_UPDATED, onLoadoutUpdated);
  UIStore.removeListener(UIStore.GAME_SUBMODE_SELECTION, onGameSubmodeSelection);
  UIStore.removeListener(UIStore.MODAL_CLOSED, onModalClosed);
  TutorialStore.removeListener(
    TutorialStore.TUTORIAL_SPOTLIGHT_VISIBLE,
    onTutorialSpotlightVisible
  );
  TutorialStore.removeListener(TutorialStore.TUTORIAL_SPOTLIGHT_HIDDEN, onTutorialSpotlightHidden);
  TutorialStore.removeListener(TutorialStore.TUTORIAL_SKIPPED, onTutorialSkipped);

  window.removeEventListener('resize', handleResize);
  window.removeEventListener('orientationchange', handleResize);

  clearInterval(_tickersInterval);

  clearTimeout(_tutorial_timeout);
  TutorialActions.destroyTutorialBox(false);
  TutorialActions.destroyTutorialSpotlight(false);
});

const handleResize = Tools.debounce(() => {
  makeGameSubmodeSelector();
  if (state.active_caverns_run) {
    makeBattleWindow({ is_resize: true });
    if (_unit_details_panel) {
      makeUnitDetailsPanel();
    }
  } else {
    makeEngagedSquadView();
  }
}, 300);

function calcTickers() {
  const submode_data = CavernsStore.getAll().caverns_data[UIStore.getAll().current_game_submode];
  if (!submode_data) {
    return;
  }

  // calc & render remaining claim delay
  const now = Date.now();
  const time_left = submode_data.last_passive_yield_claim + FIVE_HOURS_MS - now;
  state.pending_claim_delay = time_left > 0;
  state.tickers.claim_delay_text = Tools.millisecondsToHHMMSS(time_left);
}

async function destroyBattleWindow() {
  await _battle_window?.dispose();
  _battle_window = null;
}

function destroyEngagedSquadView() {
  _engaged_squad_view?.dispose();
  _engaged_squad_view = null;
}

function destroyGameSubmodeSelector() {
  _gameSubmodeSelector?.dispose();
  _gameSubmodeSelector = null;
}

function destroySquadPuppetMouseoverPanel() {
  _squad_puppet_mouseover_panel?.destroy();
  _squad_puppet_mouseover_panel = null;
}

function destroyUnitDetailsPanel() {
  _unit_details_panel?.dispose();
  _unit_details_panel = null;
}

function makeBattleWindow({ is_resize = false } = {}) {
  _battle_window?.dispose(!is_resize);
  const dom_bounding_rect = CanvasTools.getDomAnchor('caverns_battle_window');
  _battle_window = new CavernsBattleWindow({ dom_bounding_rect, is_resize });
  _battle_window.x = dom_bounding_rect.x;
  _battle_window.y = dom_bounding_rect.y;
  DT_CANVAS_GLOBALS.stage.addChildAt(_battle_window, 1);
}

function makeEngagedSquadView({ do_transition_in = true } = {}) {
  destroyEngagedSquadView();

  const dom_anchor = document.getElementById('caverns_engaged_heroes_view');
  if (!dom_anchor) {
    return;
  }
  const { x, y, width, height } = dom_anchor.getBoundingClientRect();

  const { loadouts } = GameStateStore.getAll().gameState;
  const { current_game_mode, current_game_submode } = UIStore.getAll();

  const num_heroes = getNumHeroesInSubmode(current_game_submode);
  const puppet_scale =
    (num_heroes < 5 ? 1.5 : num_heroes > 5 ? 1.15 : 1.35) * window.innerHeight * 0.0013;

  _engaged_squad_view = new EngagedSquadView({
    hero_builds: HeroBuildStore.getAll().hero_builds,
    loadout_squad_list: loadouts[current_game_mode][current_game_submode].filter(
      (hero) => !!hero.engagement
    ),
    roster_heroes: GameStateStore.getAll().gameState.hero_roster,
    puppet_scale,
    do_transition_in,
    getHorizontalPuppetSpacing: ({ puppet_index }) => {
      return Math.round(
        puppet_scale * 35 + puppet_scale * width * 0.012 + puppet_index * puppet_scale * 95
      );
    },
  });
  _engaged_squad_view.x = x + width / 2 - _engaged_squad_view._width / 2;
  _engaged_squad_view.y = y + height / 2 - DT_CANVAS_GLOBALS.spacing * 3;
  DT_CANVAS_GLOBALS.stage.addChild(_engaged_squad_view);

  // add squad puppet interaction listeners
  const puppets = _engaged_squad_view.getPuppets();
  for (const puppet of puppets) {
    puppet.mouseover = onSquadPuppetMouseover.bind(null, puppet);
    puppet.mouseout = onSquadPuppetMouseout;
  }

  // disable interaction at the "enter_caverns" tutorial step
  const { entered_caverns, skipped_all } = TutorialStore.getCompletedTutorialSteps();
  if (!entered_caverns && !skipped_all) {
    _engaged_squad_view.interactiveChildren = false;
  }
}

function makeGameSubmodeSelector() {
  destroyGameSubmodeSelector();
  _gameSubmodeSelector = new GameSubmodeSelector();
  _gameSubmodeSelector.x = window.innerWidth / 2 - _gameSubmodeSelector.width / 2;
  _gameSubmodeSelector.y = 42;
  DT_CANVAS_GLOBALS.stage.addChild(_gameSubmodeSelector);
}

function makeSquadPuppetMouseoverPanel(puppet) {
  const out_build = puppet.actor;
  const { current_game_mode, current_game_submode } = UIStore.getAll();
  const loadout =
    GameStateStore.getAll().gameState.loadouts[current_game_mode][current_game_submode];

  _squad_puppet_mouseover_panel = new SquadPuppetMouseoverPanel({
    onHeroBuildSelected: ({ hero_build_id, hero_handle }) => {
      HeroBuildActions.engageHeroBuild({
        hero_build_id,
        hero_handle,
        game_mode: current_game_mode,
        game_submode: current_game_submode,
        target_slot_index: loadout.find(({ hero_build_id: id }) => id === (out_build.hero_build_id || out_build._id)).engagement
          .slot,
      });
    },
    out_build,
  });
  puppet.addChild(_squad_puppet_mouseover_panel);
}

function makeUnitDetailsPanel() {
  destroyUnitDetailsPanel();

  const { loadouts } = GameStateStore.getAll().gameState;
  const { current_game_mode, current_game_submode } = UIStore.getAll();

  _unit_details_panel = new UnitDetailsPanel({
    loadout_squad_list: loadouts[current_game_mode][current_game_submode].filter(
      (hero) => !!hero.engagement
    ),
    onUnitViewsCreated: (this_panel) => {
      const dom_anchor = document.getElementById('caverns_left_sidebar');
      if (!dom_anchor) return;

      const { x, y, height } = dom_anchor.getBoundingClientRect();
      this_panel.x = Math.round(x + DT_CANVAS_GLOBALS.spacing * 1);
      this_panel.y = Math.round(y + height / 2 - this_panel.height * 0.47);
      DT_CANVAS_GLOBALS.stage.addChild(this_panel);
    },
  });
}

function onDescendCavernsAt1Click() {
  state.pending_enter_caverns = true;
  CavernsActions.initNewCavernsMap(1);
  _gameSubmodeSelector.muteButtons();
}

function onDescendCavernsAtPortalLevelClick() {
  state.pending_enter_caverns = true;
  CavernsActions.initNewCavernsMap(state.portal_level);
  _gameSubmodeSelector.muteButtons();
}

function onEnteredCaverns(new_caverns_data) {
  state.pending_enter_caverns = false;

  // complete tutorial step 2 - enter caverns
  const { entered_caverns, skipped_all } = TutorialStore.getCompletedTutorialSteps();
  if (!entered_caverns && !skipped_all) {
    TutorialActions.destroyTutorialSpotlight();
    TutorialActions.logTutorialStepCompleted('entered_caverns', 'onboard_step:3');

    // tutorial step 3 - caverns & basic mechanics explained
    _tutorial_timeout = setTimeout(() => {
      // tutorial step 3a
      TutorialActions.makeTutorialBox({
        text: text('tutorial.caverns_explained_1'),
        x: Math.round(window.innerWidth * 0.19),
        y: Math.round(window.innerHeight * 0.6),
        width: 275,
        arrow: 'none',
      });
      if (process.env.SEND_GAME_ANALYTICS)
        firebase.analytics().logEvent('onboard_step:3a', {
          name: 'caverns_explained_1',
        });
      _tutorial_timeout = setTimeout(() => {
        // tutorial step 3b
        TutorialActions.hideTutorialBox();
        TutorialActions.makeTutorialBox({
          text: text('tutorial.caverns_explained_2'),
          x: Math.round(window.innerWidth * 0.145 + 35),
          y: Math.round(window.innerHeight * 0.4 + 15),
          width: 225,
          arrow: 'up',
        });
        if (process.env.SEND_GAME_ANALYTICS)
          firebase.analytics().logEvent('onboard_step:3b', {
            name: 'caverns_explained_2',
          });

        _tutorial_timeout = setTimeout(() => {
          // tutorial step 3c
          TutorialActions.hideTutorialBox();
          TutorialActions.makeTutorialBox({
            text: text('tutorial.caverns_explained_3'),
            x: Math.round(window.innerWidth * 0.8),
            y: Math.round(window.innerHeight * 0.5),
            width: 225,
            arrow: 'none',
          });
          if (process.env.SEND_GAME_ANALYTICS)
            firebase.analytics().logEvent('onboard_step:3c', {
              name: 'caverns_explained_3',
            });

          _tutorial_timeout = setTimeout(() => {
            // tutorial step 3d
            TutorialActions.hideTutorialBox();
            TutorialActions.makeTutorialBox({
              text: text('tutorial.caverns_explained_4'),
              x: Math.round(window.innerWidth - 212),
              y: Math.round(window.innerHeight - 65),
              arrow: 'right',
            });
            if (process.env.SEND_GAME_ANALYTICS)
              firebase.analytics().logEvent('onboard_step:3d', {
                name: 'caverns_explained_4',
              });
          }, 12000);
        }, 12000);
      }, 8000);
    }, 3000);
  }

  CavernsActions.startCavernsBattleEngine({ new_caverns_data });
  destroyEngagedSquadView();
  state.active_caverns_run = true;
  _gameSubmodeSelector.muteButtons();
  nextTick(() => {
    // init the caverns gameplay view
    makeBattleWindow();
    Audio.setBGTrack('caverns_theme');
    clearInterval(_tickersInterval);
  });
}

function onCavernsTravelSuccess() {
  state.deepest_level = caverns_data[state.current_game_submode]?.deepest_level;
}

function onGameSubmodeSelection() {
  const { caverns_data } = CavernsStore.getAll();
  state.current_game_submode = UIStore.getAll().current_game_submode;
  state.portal_level = caverns_data[state.current_game_submode]?.portal_level;
  state.deepest_level = caverns_data[state.current_game_submode]?.deepest_level;
  nextTick(() => {
    calcTickers();
    makeEngagedSquadView();
  });
}

function onLoadoutUpdated() {
  makeEngagedSquadView();
}

function onPortalLevelSet({ new_portal_level }) {
  state.portal_level = new_portal_level;
}

async function onReturnedToSurface(completed_caverns_run_data) {
  clearTimeout(_tutorial_timeout);
  TutorialActions.hideTutorialBox();

  _gameSubmodeSelector.unmuteButtons();
  Audio.setBGTrack('menu_music');

  // wait for battle_tiles_view to fully dispose before proceeding
  await destroyBattleWindow();
  destroyUnitDetailsPanel();

  // hide battle view and update state
  state.active_caverns_run = false;
  state.portal_level = CavernsStore.getAll().caverns_data[state.current_game_submode].portal_level;

  // show BattleDebriefModal (after battle view is cleaned up)
  state.showing_debrief_modal = true;
  UIActions.showModal({
    modal_key: 'BattleDebriefModal',
    modal_props: {
      completed_caverns_run_data,
    },
  });

  nextTick(() => {
    _tickersInterval = setInterval(calcTickers, 1000);
    calcTickers();
  });
}

function onSquadPuppetMouseout() {
  destroySquadPuppetMouseoverPanel();
}

function onSquadPuppetMouseover(puppet) {
  makeSquadPuppetMouseoverPanel(puppet);
}

function onModalClosed() {
  if (state.showing_debrief_modal) {
    state.showing_debrief_modal = false;
    makeEngagedSquadView();
  }

  // tutorial step 4 - navigate to edit hero
  const { entered_caverns, navigated_to_edit_hero, skipped_all } =
    TutorialStore.getCompletedTutorialSteps();
  if (entered_caverns && !navigated_to_edit_hero && !skipped_all) {
    setTimeout(() => {
      const first_puppet = _engaged_squad_view.getPuppets()[0];
      const spotlight = TutorialActions.makeTutorialSpotlight({
        radius: 160,
        x: _engaged_squad_view.x + first_puppet.x,
        y: _engaged_squad_view.y + first_puppet.y - _engaged_squad_view.puppet_scale * 5,
        onTransitionInComplete: () => {
          spotlight &&
            TutorialActions.makeTutorialBox({
              text: text('tutorial.nav_to_edit_hero'),
              x: Math.round(spotlight.x + _engaged_squad_view.puppet_scale * 13),
              y: Math.round(spotlight.y - _engaged_squad_view.puppet_scale * 20),
              arrow: 'left',
              width: 220,
            });
        },
      });
    }, 500);
  }
}

function onTutorialSpotlightVisible() {
  const { entered_caverns, navigated_to_edit_hero } = TutorialStore.getCompletedTutorialSteps();
  if (!entered_caverns && _engaged_squad_view) {
    _engaged_squad_view.interactiveChildren = false;
  }
  if (entered_caverns && !navigated_to_edit_hero) {
    state.is_on_tutorial_step_nav_to_edit_hero = true;
  }
}

function onTutorialSpotlightHidden() {
  if (_engaged_squad_view) {
    _engaged_squad_view.interactiveChildren = true;
  }
  state.is_on_tutorial_step_nav_to_edit_hero = false;
}

function onPassiveYieldsClaimed() {
  // giving time for calcTickers to run
  setTimeout(() => {
    state.pending_claim_call = false;
  }, 1000);
}

function onAPIError() {
  state.pending_enter_caverns = false;
  state.pending_claim_call = false;
  _gameSubmodeSelector.unmuteButtons();
}

function onCavernsBattleInitialized() {
  setTimeout(makeUnitDetailsPanel, CAVERNS_BATTLE_INIT_DELAY);
}

function onInsufficientGold() {
  state.pending_enter_caverns = false;
}

function onTutorialSkipped() {
  clearTimeout(_tutorial_timeout);
  if (_engaged_squad_view) {
    _engaged_squad_view.interactiveChildren = true;
  }
}
</script>
